import {
  Alignment,
  Autoformat,
  Bold,
  Code,
  Italic,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
  BlockQuote,
  CodeBlock,
  EditorConfig,
  PluginConstructor,
  Essentials,
  FontBackgroundColor,
  FontColor,
  DataFilter,
  DataSchema,
  GeneralHtmlSupport,
  Image,
  ImageCaption,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  AutoLink,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SpecialCharacters,
  SpecialCharactersEssentials,
  SpecialCharactersMathematical,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  WordCount,
} from "ckeditor5";
import { produce } from "immer";
import { remove } from "lodash";

import { IconResetFormatLine } from "@examspark/common-ui/dist/components/icons";

import { RichContentConfig } from "./contexts/RichContentConfigProvider";
import { RichContentOptions } from "./contexts/RichContentOptionsProvider";
import AdditionalContext, {
  UI_BUTTON_CONTEXT,
} from "./plugins/AdditionalContext";
import Analytics from "./plugins/Analytics";
import ConvertLatexToEquations, {
  UI_BUTTON_RECOGNIZE_LATEX,
} from "./plugins/ConvertLatexToEquations";
import DataApiEventDecorator from "./plugins/DataApiEventDecorator";
import FixMalformedListsOnPaste from "./plugins/FixMalformedListsOnPaste";
import HtmlSource, { UI_BUTTON_HTML_SOURCE } from "./plugins/HtmlSource";
import ImageAlignVertical, {
  UI_BUTTON_IMAGE_INLINE_BOTTOM,
  UI_BUTTON_IMAGE_INLINE_MIDDLE,
  UI_BUTTON_IMAGE_INLINE_TOP,
} from "./plugins/ImageAlignVertical";
import InspectorHelper from "./plugins/InspectorHelper";
import ItemEditorGap, { UI_BUTTON_GAP } from "./plugins/ItemEditorGap";
import ItemEditorHotText, {
  UI_BUTTON_HOTTEXT,
} from "./plugins/ItemEditorHotText";
import LegacyMath from "./plugins/LegacyMath";
import MarkBlocks from "./plugins/MarkBlocks";
import MathType from "./plugins/MathType";
import MutableClipboardData from "./plugins/MutableClipboardData";
import { Nowrap, UI_BUTTON_NOWRAP } from "./plugins/Nowrap";
import PasteFromMathType from "./plugins/PasteFromMathType";
import PrettyHtmlData from "./plugins/PrettyHtmlData";
import ResetListStyles, {
  UI_BUTTON_RESET_LIST_STYLES,
} from "./plugins/ResetListStyles";
import ResetTableStyles, {
  UI_BUTTON_RESET_TABLE_STYLES,
} from "./plugins/ResetTableStyles";
import SetImageSize from "./plugins/SetImageSize";
import SharedResourceInsert, {
  UI_BUTTON_INSERT_SHARED_RESOURCE,
} from "./plugins/SharedResourceInsert";
import SingleLine from "./plugins/SingleLine";
import TableBlockAlignment from "./plugins/TableBlockAlignment";
import TableStyles, { UI_DROPDOWN_TABLE_STYLES } from "./plugins/TableStyles";
import ToastNotifications from "./plugins/ToastNotifications";
import { UiOverrides } from "./plugins/UiOverrides";
import UnsupportedContent from "./plugins/UnsupportedContent";
import UploadWithContext from "./plugins/UploadWithContext";

export const createConfiguration = (
  options: RichContentOptions,
  config: RichContentConfig | undefined,
): EditorConfig => {
  options = validateOptions(options, config);

  const editableMarkBlocks = Object.entries(options?.tools?.markBlocks ?? {})
    .filter(([, value]) => !value.readonly)
    .map(([key]) => key);

  const editorConfig: EditorConfig = {
    plugins: getPluginList(),
    toolbar: removeContiguousDuplicates(
      buildToolbar(
        "bold",
        "italic",
        "underline",
        "strikethrough",
        options?.tools?.advancedFormatting && [
          "subscript",
          "superscript",
          UI_BUTTON_NOWRAP,
          "link",
          "code",
          "fontColor",
          "fontBackgroundColor",
          "removeFormat",
          "specialCharacters",
        ],
        "|",
        !!options.tools?.itemEditor?.additionalContext && UI_BUTTON_CONTEXT,
        !!options.tools?.itemEditor?.gaps && [UI_BUTTON_GAP],
        !!options.tools?.itemEditor?.hottext && [UI_BUTTON_HOTTEXT],
        "|",
        !options?.singleLine && [
          options.tools?.blockFormatting && [
            "alignment",
            "bulletedList",
            "numberedList",
            UI_BUTTON_RESET_LIST_STYLES,
            "outdent",
            "indent",
            "codeBlock",
            "blockQuote",
            "insertTable",
          ],
          editableMarkBlocks.length > 0 && ["|", ...editableMarkBlocks],
        ],
        "|",
        options.tools?.advancedFormatting && ["insertImage"],
        options.tools?.sharedResources &&
          config?.sharedResourceApi != null && [
            UI_BUTTON_INSERT_SHARED_RESOURCE,
          ],
        "|",
        options?.tools?.math && [
          "MathType",
          "ChemType",
          UI_BUTTON_RECOGNIZE_LATEX,
        ],
        "|",
        options.tools?.htmlEditor && [UI_BUTTON_HTML_SOURCE],
        "|",
        "undo",
        "redo",
      ),
    ),
    language: "en",
    image: {
      resizeUnit: "px",
      toolbar: buildToolbar([
        "imageTextAlternative",
        !options.singleLine && "toggleImageCaption",
        UI_BUTTON_IMAGE_INLINE_BOTTOM,
        UI_BUTTON_IMAGE_INLINE_MIDDLE,
        UI_BUTTON_IMAGE_INLINE_TOP,
        !options.singleLine && ["imageStyle:block", "imageStyle:side"],
        "linkImage",
        "setImageSize",
      ]),
      insert: { type: "inline" },
    },
    table: {
      contentToolbar: buildToolbar([
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableCellProperties",
        "tableProperties",
        "toggleTableCaption",
        UI_BUTTON_RESET_TABLE_STYLES,
        config?.tableStyles != null && UI_DROPDOWN_TABLE_STYLES,
      ]),
      tableProperties: {
        defaultProperties: {
          // these must match the default table styles in common-ui/src/utils/styles.ts
          borderColor: "transparent",
          borderStyle: "double",
          borderWidth: "1px",
          alignment: "left",
        },
      },
      tableCellProperties: {
        defaultProperties: {
          // these must match the default table styles in common-ui/src/utils/styles.ts
          borderColor: "transparent",
          borderStyle: "solid",
          borderWidth: "1px",
        },
      },
    },
    tableStyles: {
      definitions: config?.tableStyles,
    },
    codeBlock: {
      languages: [{ language: "plaintext", label: "Plain Text", class: "" }],
    },
    list: {
      properties: {
        startIndex: true,
        reversed: true,
      },
    },

    uiOverrides: {
      removeFormat: {
        icon: IconResetFormatLine.Content,
      },
    },

    mathTypeParameters: {
      editorParameters: {
        fonts: [],
        fontFamily: "Times New Roman",
        fontSize: "12pt",
        detectHand: false,
        toolbar: /* XML */ `
          <toolbar ref='general'>
            <tab ref='matrices'>
              <removeItem ref='longDivision' />
              <removeItem ref='3RowsStackWithLineAndPlusSign' />
            </tab>
            <tab ref='contextual'>
              <removeItem ref='carries' />
            </tab>
          </toolbar>
        `.replace(/\n\s+/g, ""), // "minify" so wiris's crappy xml parser will understand
      },
    },

    markBlocks: options.tools?.markBlocks,
    detectUnsupportedHtml: options.detectUnsupportedHtml,
    singleLine: options.singleLine,

    fileUploadApi: config?.fileUploadApi,
    sharedResourceApi: config?.sharedResourceApi,

    enableInspector: config?.enableInspector,
    licenseKey:
      config?.licenseKey ?? process.env.REACT_APP_CKEDITOR_LICENSE_KEY,
    analytics: { onEvent: config?.onAnalyticsEvent },

    allowEditSource: true,
  };

  switch (options?.allowUnsupportedHtml) {
    case "minimal":
      editorConfig.htmlSupport = {
        allow: [
          { name: "li", styles: "list-style-type" },
          { name: /div|span/ },
          {
            name: "span",
            styles: {
              "white-space": /nowrap/,
            },
          },
        ],
      };
      break;
    case "all":
      editorConfig.htmlSupport = {
        allow: [
          {
            name: /.*/,
            attributes: true,
            styles: true,
            classes: true,
          },
        ],
      };
      break;
  }

  if (!options.tools?.imageUpload) {
    //
    // If the ImageUpload plugin is installed, we will get a button that lets us
    // browse and try to upload, even if the upload adapter isn't configured
    //
    remove(editorConfig.plugins ?? [], (p) => p === ImageUpload);
  }

  config?.onConfig?.(editorConfig);

  return editorConfig;
};

type ToolbarBuilder = Array<
  string | false | undefined | string[] | ToolbarBuilder
>;

const buildToolbar = (...items: ToolbarBuilder): string[] => {
  return items.flatMap((item) => {
    if (item === false || item === undefined) return [];
    if (Array.isArray(item)) return buildToolbar(...item);
    return [item];
  });
};

const removeContiguousDuplicates = (items: string[]) =>
  items.filter((item, idx) => item !== items[idx - 1]);

const validateOptions = (
  options: RichContentOptions,
  config: RichContentConfig | undefined,
) => {
  return produce(options, (draft) => {
    if (draft.singleLine && draft.tools?.blockFormatting) {
      console.warn(
        "rce_options_invalid",
        `cannot enable blockFormatting in singleLine mode`,
      );
      draft.tools.blockFormatting = false;
    }

    if (draft.singleLine && draft.allowUnsupportedHtml === "all") {
      console.warn(
        "rce_options_invalid",
        `cannot enable allowUnsupportedHtml="all" in singleLine mode`,
      );
      draft.allowUnsupportedHtml = "none";
    }

    if (draft.tools?.sharedResources && config?.sharedResourceApi == null) {
      console.warn(
        "rce_options_invalid",
        `cannot enable sharedResources because no sharedResourcesApi is configured`,
      );
      draft.tools.sharedResources = false;
    }

    if (!draft.tools?.sharedResources && config?.sharedResourceApi) {
      console.info(
        "rce_options",
        "sharedResourceApi is configured, but no tools that use it are enabled",
      );
    }

    if (draft.tools?.imageUpload && config?.fileUploadApi == null) {
      console.warn(
        "rce_options_invalid",
        `cannot enable imageUpload because no fileUploadApi is configured`,
      );
      draft.tools.imageUpload = false;
    }

    if (draft.tools?.imageUpload && !draft.tools?.advancedFormatting) {
      console.warn(
        "rce_options_invalid",
        `cannot enable imageUpload when advancedFormatting is not enabled`,
      );
    }

    if (!draft.tools?.imageUpload && config?.fileUploadApi) {
      console.info(
        "rce_options",
        "fileUploadApi is configured, but no tools that use it are enabled",
      );
    }
  });
};

const getPluginList = (): Array<PluginConstructor> => {
  return [
    //
    // Stock plugins
    //
    Alignment,
    Analytics,
    AutoLink,
    Autoformat,
    Bold,
    BlockQuote,
    Code,
    CodeBlock,
    DataFilter,
    DataSchema,
    List,
    ListProperties,
    Essentials,
    FontBackgroundColor,
    FontColor,
    GeneralHtmlSupport,
    Image,
    ImageCaption,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    MediaEmbed,
    PasteFromOffice,
    RemoveFormat,
    SelectAll,
    ShowBlocks,
    SpecialCharacters,
    SpecialCharactersEssentials,
    SpecialCharactersMathematical,
    Strikethrough,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    Underline,
    WordCount,

    //
    // Custom plugins
    //
    AdditionalContext,
    ConvertLatexToEquations,
    DataApiEventDecorator,
    FixMalformedListsOnPaste,
    HtmlSource,
    ImageAlignVertical,
    InspectorHelper,
    ItemEditorGap,
    ItemEditorHotText,
    LegacyMath,
    MarkBlocks,
    MathType,
    MutableClipboardData,
    Nowrap,
    PasteFromMathType,
    PrettyHtmlData,
    ResetListStyles,
    ResetTableStyles,
    SharedResourceInsert,
    SetImageSize,
    SingleLine,
    TableBlockAlignment,
    TableStyles,
    ToastNotifications,
    UiOverrides,
    UnsupportedContent,
    UploadWithContext,
  ];
};
